// client/src/dnd/views/MapView.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Sidebar from './Sidebar';
import InteractiveMap from './InteractiveMap';
import SaveLayerModal from '../components/SaveLayerModal';
import IconUpload from '../components/IconUpload';
import logo from '../../assets/logo128x128.png';
import 'leaflet/dist/leaflet.css';
import '../components/leaflet-geoman.css';
import './MapView.css';

const API_BASE_URL = window.location.origin;

const SIDEBAR_WIDTH_KEY = "point_atlas_dnd_sidebar_width_v1";
const SIDEBAR_MIN_WIDTH = 150;
const SIDEBAR_MAX_WIDTH = 600;
const SIDEBAR_DEFAULT_WIDTH = 300;

function clamp(n, min, max) {
  const x = Number(n);
  if (!Number.isFinite(x)) return min;
  return Math.max(min, Math.min(max, x));
}

function useMobile(threshold = 800) {
  const [mobile, setMobile] = useState(window.innerWidth <= threshold);
  useEffect(() => {
    const handleResize = () => setMobile(window.innerWidth <= threshold);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [threshold]);
  return mobile;
}

function MapView() {
  const params = useParams();
  const navigate = useNavigate();
  let mapId = params.id || params.mapId;
  if (mapId && typeof mapId === "string") {
    const match = mapId.match(/(\d+)$/);
    if (match) mapId = match[1];
  }

  const [mapData, setMapData] = useState(null);
  const [parentMapData, setParentMapData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [imageSize, setImageSize] = useState(null);
  const [pois, setPois] = useState([]);
  const [pendingPOI, setPendingPOI] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [shape, setShape] = useState('circle');
  const [customCategories, setCustomCategories] = useState([]);
  const [customTypes, setCustomTypes] = useState({});
  const [typeColours, setTypeColours] = useState({});
  const [typeColourInput, setTypeColourInput] = useState('#ff0000');
  const [visibleTypes, setVisibleTypes] = useState(new Set());
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
      const v = parseInt(raw || "", 10);
      if (Number.isFinite(v)) return clamp(v, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
    } catch {}
    return SIDEBAR_DEFAULT_WIDTH;
  });
  const [hoveredType, setHoveredType] = useState(null);
  const [hoveredPoiId, setHoveredPoiId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [voteStatus, setVoteStatus] = useState({ total_votes: 0, user_vote: 0 });
  const [isFavourited, setIsFavourited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const sidebarRef = useRef(null);
  const [leafletMap, setLeafletMap] = useState(null);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mouseCoord, setMouseCoord] = useState(null);
  const [shareCoord, setShareCoord] = useState(null);
  const [copiedCoord, setCopiedCoord] = useState(false);
  const [showAllPoiLabels, setShowAllPoiLabels] = useState(false);
  const [foundPOIs, setFoundPOIs] = useState([]);
  const [showFound, setShowFound] = useState(false);
  const mainMapRef = useRef();
  const dropZoneRef = useRef(null);
  const lastMouseCoordRef = useRef(null);
  const [poiName, setPoiName] = useState('');
  const isMobile = useMobile(800);
  const [mobileDrawer, setMobileDrawer] = useState(null);
  const [flashType, setFlashType] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [pencilSize, setPencilSize] = useState(4);
  const [fill, setFill] = useState('#d43a3a');
  const [opacity, setOpacity] = useState(0.7);
  const [pattern, setPattern] = useState('none');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const handleUndo = () => {};
  const handleRedo = () => {};
  const [showSaveLayerModal, setShowSaveLayerModal] = useState(false);
  const [pendingLayer, setPendingLayer] = useState(null);
  const [zones, setZones] = useState([]);
  const [visibleZones, setVisibleZones] = useState(new Set());
  const [zonesExpanded, setZonesExpanded] = useState(true);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  const [hideSidebarHover, setHideSidebarHover] = useState(false);
  const [extraRegionMaps, setExtraRegionMaps] = useState([]);
  const [communityMapId, setCommunityMapId] = useState(null);
  const [communityMapData, setCommunityMapData] = useState(null);
  const [mapKey, setMapKey] = useState(mapId);
  const [isPublic, setIsPublic] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pubStatusMsg, setPubStatusMsg] = useState('');
  const [proximityVoiceEnabled, setProximityVoiceEnabled] = useState(false);
  const [proximityVoiceSaving, setProximityVoiceSaving] = useState(false);
  const [proximityVoiceStatus, setProximityVoiceStatus] = useState('');
  const [mapPresenceUsers, setMapPresenceUsers] = useState([]);
  const [mapPresenceUpdatedAt, setMapPresenceUpdatedAt] = useState(0);
  const presenceSessionIdRef = useRef(null);

  const onlineMapUserIds = useMemo(() => {
    return new Set(
      (Array.isArray(mapPresenceUsers) ? mapPresenceUsers : [])
        .map(u => Number(u?.id))
        .filter(id => Number.isFinite(id) && id > 0)
    );
  }, [mapPresenceUsers]);

  useEffect(() => {
    try {
      const key = 'pa_tabletop_presence_session_id';
      let sid = localStorage.getItem(key);
      if (!sid || typeof sid !== 'string' || sid.length < 8) {
        sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
          .replace(/[^a-z0-9\-]/gi, '')
          .slice(0, 64);
        localStorage.setItem(key, sid);
      }
      presenceSessionIdRef.current = sid;
    } catch {
      presenceSessionIdRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`.slice(0, 64);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamp(sidebarWidth, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH)));
    } catch {}
  }, [sidebarWidth]);

  useEffect(() => {
    if (!leafletMap) return;
    try {
      setTimeout(() => {
        try {
          leafletMap.invalidateSize();
        } catch {}
      }, 0);
    } catch {}
  }, [leafletMap, sidebarOpen, sidebarWidth]);

  useEffect(() => {
    if (!leafletMap || isMobile) return;

    const mapContainer = leafletMap.getContainer();
    if (!mapContainer) return;

    const updateControlPositions = () => {
      try {
        const sidebarEl = sidebarRef.current;
        const pmToolbar = mapContainer.querySelector('.leaflet-pm-toolbar.leaflet-control');
        const zoomControl = mapContainer.querySelector('.leaflet-control-zoom');

        const sidebarWidthPx =
          sidebarOpen && sidebarEl
            ? sidebarEl.getBoundingClientRect().width
            : 0;

        const offset = sidebarWidthPx + 10;

        if (mapContainer && mapContainer.style) {
          mapContainer.style.zIndex = '3000';
        }

        if (pmToolbar) {
          pmToolbar.style.position = 'absolute';
          pmToolbar.style.top = '10px';
          pmToolbar.style.left = `${offset}px`;
          pmToolbar.style.marginLeft = '0px';
          pmToolbar.style.marginTop = '0px';
          pmToolbar.style.float = 'none';
          pmToolbar.style.zIndex = '3002';
          pmToolbar.style.pointerEvents = 'auto';
        }

        if (zoomControl) {
          zoomControl.style.position = 'absolute';
          zoomControl.style.right = '10px';
          zoomControl.style.bottom = '10px';
          zoomControl.style.left = 'auto';
          zoomControl.style.top = 'auto';
          zoomControl.style.margin = '0';
          zoomControl.style.float = 'none';
          zoomControl.style.zIndex = '3002';
          zoomControl.style.pointerEvents = 'auto';
        }
      } catch {}
    };

    updateControlPositions();
    window.addEventListener('resize', updateControlPositions);
    return () => {
      window.removeEventListener('resize', updateControlPositions);
    };
  }, [leafletMap, sidebarOpen, sidebarWidth, isMobile]);

  useEffect(() => {
    setMapKey(`${mapId}-${Date.now()}`);
  }, [mapId]);

  useEffect(() => {
    const key = `foundPois_${mapId}`;
    const val = localStorage.getItem(key);
    if (val) setFoundPOIs(JSON.parse(val));
    else setFoundPOIs([]);
  }, [mapId]);

  const saveFoundPOIs = (ids) => {
    const key = `foundPois_${mapId}`;
    localStorage.setItem(key, JSON.stringify(ids));
    setFoundPOIs(ids);
  };

  const togglePOIFound = (poiId) => {
    let newFound;
    if (foundPOIs.includes(poiId)) {
      newFound = foundPOIs.filter(id => id !== poiId);
    } else {
      newFound = [...foundPOIs, poiId];
    }
    saveFoundPOIs(newFound);
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/me`, { credentials: 'include' })
      .then(res => res.json())
      .then(user => setUser(user))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!mapId) return;
    fetch(`${API_BASE_URL}/api/maps/${mapId}`)
      .then(res => res.json())
      .then(data => {
        setMapData(data);
        setProximityVoiceEnabled(!!data.proximity_voice_enabled);
        setProximityVoiceStatus('');
        if (data.parent_map_id) {
          fetch(`${API_BASE_URL}/api/maps/${data.parent_map_id}`)
            .then(res => res.json())
            .then(parent => {
              setParentMapData(parent);
              setCommunityMapId(parent.id);
              setCommunityMapData(parent);
              setExtraRegionMaps(parent.extra_region_maps || []);
              setIsPublic(!!parent.is_public);
            });
        } else {
          setCommunityMapId(data.id);
          setCommunityMapData(data);
          setExtraRegionMaps(data.extra_region_maps || []);
          setIsPublic(!!data.is_public);
        }

        const rawUrl = data.image_url || '';
        const absUrl = rawUrl.startsWith('http') ? rawUrl : `${API_BASE_URL}${rawUrl}`;

        if (/\.(mp4|webm|ogg)$/i.test(absUrl)) {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.onloadedmetadata = () => {
            const fixedHeight = 1080;
            const scale = fixedHeight / (video.videoHeight || 1);
            const calculatedWidth = Math.round((video.videoWidth || 0) * scale);
            setImageSize([calculatedWidth, fixedHeight]);
          };
          video.onerror = () => {
            setImageSize([1920, 1080]);
          };
          video.src = absUrl;
        } else {
          const img = new window.Image();
          img.onload = () => {
            const iw = img.naturalWidth || img.width || 0;
            const ih = img.naturalHeight || img.height || 0;
            setImageSize([iw, ih]);
          };
          img.onerror = () => {
            setImageSize([1920, 1080]);
          };
          img.src = absUrl;
        }

        if (data.game_id && data.mode !== 'dnd') {
          fetch(`${API_BASE_URL}/api/games/${data.game_id}`)
            .then(res => {
              if (!res.ok) throw new Error('Game not found');
              return res.json();
            })
            .then(game => setGameData(game))
            .catch(() => setGameData(null));
        } else {
          setGameData(null);
        }
      });
    loadPOIs();
    loadZones();
  }, [mapId]);

  useEffect(() => {
    if (!mapId || !user?.id) {
      setMapPresenceUsers([]);
      setMapPresenceUpdatedAt(0);
      return;
    }

    let alive = true;
    let pingTimer = null;
    let pollTimer = null;

    const leaveMapPresence = () => {
      try {
        const sid = presenceSessionIdRef.current || '';
        const payload = JSON.stringify({ session_id: sid });
        const url = `${API_BASE_URL}/api/tabletop/maps/${mapId}/presence/leave`;

        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
          return;
        }

        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          keepalive: true,
          body: payload,
        }).catch(() => {});
      } catch {}
    };

    const pingMapPresence = async () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      } catch {}
      try {
        await fetch(`${API_BASE_URL}/api/tabletop/maps/${mapId}/presence/ping`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            session_id: presenceSessionIdRef.current || '',
          })
        });
      } catch {}
    };

    const pollMapPresence = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/tabletop/maps/${mapId}/presence/online?within_ms=90000`, {
          credentials: 'include'
        });
        if (!res.ok) throw new Error('Failed to fetch map presence');
        const data = await res.json();
        if (!alive) return;

        const rawUsers =
          Array.isArray(data?.users) ? data.users
          : Array.isArray(data?.online_users) ? data.online_users
          : Array.isArray(data) ? data
          : [];

        setMapPresenceUsers(rawUsers);
        setMapPresenceUpdatedAt(Date.now());
      } catch {
        if (!alive) return;
        setMapPresenceUsers([]);
        setMapPresenceUpdatedAt(Date.now());
      }
    };

    const handleVisibilityChange = () => {
      try {
        if (document.visibilityState === 'visible') {
          pingMapPresence();
          pollMapPresence();
        }
      } catch {}
    };

    const handlePageHide = () => {
      leaveMapPresence();
    };

    const handleBeforeUnload = () => {
      leaveMapPresence();
    };

    pingMapPresence();
    pollMapPresence();

    pingTimer = setInterval(pingMapPresence, 30000);
    pollTimer = setInterval(pollMapPresence, 8000);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      alive = false;
      if (pingTimer) clearInterval(pingTimer);
      if (pollTimer) clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      leaveMapPresence();
    };
  }, [mapId, user?.id]);

  useEffect(() => {
    if (communityMapData) {
      setIsPublic(!!communityMapData.is_public);
    }
  }, [communityMapData]);

  useEffect(() => {
    if (mapData) {
      setProximityVoiceEnabled(!!mapData.proximity_voice_enabled);
    }
  }, [mapData]);

  useEffect(() => {
    const node = mainMapRef.current;
    if (!node) return;
    const handleMouseLeave = () => setHoveredPoiId(null);
    node.addEventListener('mouseleave', handleMouseLeave);
    return () => node.removeEventListener('mouseleave', handleMouseLeave);
  }, [mainMapRef]);

  useEffect(() => {
    if (!communityMapId || !user) return;
    fetch(`${API_BASE_URL}/api/map-votes/${communityMapId}/total`)
      .then(res => res.json())
      .then(totalData => {
        fetch(`${API_BASE_URL}/api/map-votes/${communityMapId}/${user.id}`)
          .then(res => res.json())
          .then(userData => {
            setVoteStatus({
              total_votes: totalData.total_votes,
              user_vote: userData.user_vote
            });
          })
          .catch(() => {
            setVoteStatus({ total_votes: totalData.total_votes, user_vote: 0 });
          });
      })
      .catch(() => setVoteStatus({ total_votes: 0, user_vote: 0 }));
    fetch(`${API_BASE_URL}/api/user/favourites`, { credentials: 'include' })
      .then(res => res.json())
      .then(favs => {
        setIsFavourited(favs.some(fav => parseInt(fav.id) === parseInt(communityMapId)));
      })
      .catch(() => setIsFavourited(false));
  }, [user, communityMapId]);

  const handleFavourite = async () => {
    if (!user) return alert("You must be logged in to favourite maps!");
    setFavLoading(true);
    try {
      if (isFavourited) {
        await fetch(`${API_BASE_URL}/api/user/favourites/${communityMapId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setIsFavourited(false);
      } else {
        await fetch(`${API_BASE_URL}/api/user/favourites/${communityMapId}`, {
          method: 'POST',
          credentials: 'include'
        });
        setIsFavourited(true);
      }
    } finally {
      setFavLoading(false);
    }
  };

  const handleVote = async (vote) => {
    if (!user) return alert("You must be logged in to vote!");
    await fetch(`${API_BASE_URL}/api/map-votes/${communityMapId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, vote }),
    });
    fetch(`${API_BASE_URL}/api/map-votes/${communityMapId}/${user.id}`)
      .then(res => res.json())
      .then(setVoteStatus);
  };

  function loadPOIs() {
    fetch(`${API_BASE_URL}/api/poi/all/${mapId}`)
      .then(res => res.json())
      .then(data => {
        const pois = Array.isArray(data.pois) ? data.pois : [];
        setPois(pois);
        const cats = new Set();
        const typesByCat = {};
        const colours = {};
        for (const poi of pois) {
          cats.add(poi.category);
          if (!typesByCat[poi.category]) typesByCat[poi.category] = [];
          if (!typesByCat[poi.category].includes(poi.type)) {
            typesByCat[poi.category].push(poi.type);
            colours[poi.type] = `#${(poi.color || 0).toString(16).padStart(6, '0')}`;
          }
        }
        for (const cat in typesByCat) {
          typesByCat[cat].sort((a, b) => a.localeCompare(b));
        }
        const sortedCats = Array.from(cats).sort((a, b) => a.localeCompare(b));
        setCustomCategories(sortedCats);
        setCustomTypes(typesByCat);
        setTypeColours(colours);
        setVisibleCategories(new Set(sortedCats));
        setVisibleTypes(new Set(Object.values(typesByCat).flat()));
        setExpandedCategories({});
        setExpandedTypes({});
      });
  }

  function loadZones() {
    if (!mapId) return;
    fetch(`${API_BASE_URL}/api/polygons?mapId=${mapId}`)
      .then(res => res.json())
      .then(data => {
        setZones(data);
        const allZoneIds = new Set(data.map(zone => zone.id));
        setVisibleZones(allZoneIds);
      })
      .catch(() => {
        setZones([]);
        setVisibleZones(new Set());
      });
  }

  const isMapOwnerOrFounder = user && (user.id === communityMapData?.uploader_id || user.id === 1);

  const handlePublicToggle = async (e) => {
    const newValue = e.target.checked;
    setPublishing(true);
    setPubStatusMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/maps/${communityMapId}/set-public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_public: newValue })
      });
      if (!res.ok) {
        setPubStatusMsg('Failed to update map visibility.');
      } else {
        setIsPublic(newValue);
        setPubStatusMsg(newValue ? "Your map is now PUBLIC!" : "Your map is now PRIVATE.");
        fetch(`${API_BASE_URL}/api/maps/${communityMapId}`)
          .then(res => res.json())
          .then(newCommunityMapData => {
            setCommunityMapData(newCommunityMapData);
            setIsPublic(!!newCommunityMapData.is_public);
          });
      }
    } catch {
      setPubStatusMsg('Error updating map visibility.');
    }
    setPublishing(false);
  };

  const handleProximityVoiceToggle = async (nextValue) => {
    if (!mapData?.id) return;
    setProximityVoiceSaving(true);
    setProximityVoiceStatus('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/maps/${mapData.id}/set-proximity-voice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ proximity_voice_enabled: nextValue })
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setProximityVoiceStatus(
          (data && data.error) || 'Failed to update proximity voice.'
        );
        return;
      }

      const enabled = !!(data && data.proximity_voice_enabled);
      setProximityVoiceEnabled(enabled);
      setProximityVoiceStatus(
        enabled ? 'Proximity voice enabled.' : 'Proximity voice disabled.'
      );
      setMapData((prev) => (prev ? { ...prev, proximity_voice_enabled: enabled ? 1 : 0 } : prev));
      setCommunityMapData((prev) => {
        if (!prev || prev.id !== mapData.id) return prev;
        return { ...prev, proximity_voice_enabled: enabled ? 1 : 0 };
      });
    } catch {
      setProximityVoiceStatus('Error updating proximity voice.');
    }
    setProximityVoiceSaving(false);
  };

  const handleMapCoordClick = (e) => {
    if (!isMapOwnerOrFounder && !pendingPOI) {
      setShareCoord(e);
      setCopiedCoord(false);
    }
  };

  const handleMouseCoord = (coord) => {
    setMouseCoord(coord);
    lastMouseCoordRef.current = coord;
  };

  const handleCopyCoord = (coord) => {
    if (coord) {
      const str = `X: ${Math.round(coord.x)}, Y: ${Math.round(coord.y)}`;
      navigator.clipboard.writeText(str);
      setCopiedCoord(true);
      setTimeout(() => setCopiedCoord(false), 1500);
    } else {
      setShareCoord(null);
      setCopiedCoord(false);
    }
  };

  const handleClick = (coords) => {
    setPendingPOI(coords);
    setSelectedCategory('');
    setSelectedType('');
    setDescription('');
    setTypeColourInput('#ff0000');
    setShape('circle');
    setPoiName('');
  };

  const savePOI = async (poiNameArg, imageUrlArg, iconFile, onIconUploadSuccess) => {
    if (!selectedCategory || !selectedType) return;
    const categoryRes = await fetch(`${API_BASE_URL}/api/poi/category`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: selectedCategory, map_id: parseInt(mapId), user_id: user?.id })
    });
    const { id: category_id } = await categoryRes.json();
    const typeRes = await fetch(`${API_BASE_URL}/api/poi/type`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: selectedType,
        map_id: parseInt(mapId),
        category_id,
        color: parseInt(typeColourInput.replace('#', ''), 16),
        icon: shape
      })
    });
    const { id: type_id } = await typeRes.json();
    if (iconFile && onIconUploadSuccess) {
      await onIconUploadSuccess(type_id);
    }
    await fetch(`${API_BASE_URL}/api/poi/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        map_id: parseInt(mapId),
        category_id,
        type_id,
        x: pendingPOI.x,
        y: pendingPOI.y,
        description: description.trim(),
        poi_name: poiNameArg ? poiNameArg.trim() : '',
        image_url: imageUrlArg || '',
        created_by: user?.id
      })
    });
    setPendingPOI(null);
    setPoiName('');
    loadPOIs();
  };

  const handleCategorySaved = () => {
    loadPOIs();
  };

  const handleZonesChanged = () => {
    loadZones();
  };

  const toggleType = (type) => {
    setVisibleTypes(prev => {
      const newSet = new Set(prev);
      newSet.has(type) ? newSet.delete(type) : newSet.add(type);
      return newSet;
    });
  };

  const toggleCategory = (category) => {
    setVisibleCategories(prev => {
      const newSet = new Set(prev);
      newSet.has(category) ? newSet.delete(category) : newSet.add(category);
      return newSet;
    });
  };

  const toggleAllTypes = () => {
    const allTypes = Object.values(customTypes).flat();
    const allVisible = allTypes.every(type => visibleTypes.has(type));
    setVisibleTypes(allVisible ? new Set() : new Set(allTypes));
  };

  const toggleCategoryExpand = (category, e) => {
    e.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleZone = (zoneId) => {
    setVisibleZones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(zoneId)) newSet.delete(zoneId);
      else newSet.add(zoneId);
      return newSet;
    });
  };

  const toggleAllZones = () => {
    if (visibleZones.size === zones.length) {
      setVisibleZones(new Set());
    } else {
      setVisibleZones(new Set(zones.map(zone => zone.id)));
    }
  };

  const toggleZonesExpand = () => {
    setZonesExpanded(prev => !prev);
  };

  const handleSidebarMouseDown = (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch {}

    const startX = e.clientX;
    const startWidth = sidebarRef.current ? sidebarRef.current.getBoundingClientRect().width : sidebarWidth;

    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;

    try {
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    } catch {}

    let raf = 0;

    const onMouseMove = (ev) => {
      const dx = ev.clientX - startX;
      const targetWidth = clamp(startWidth + dx, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setSidebarWidth(targetWidth));
    };

    const onMouseUp = () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      try {
        document.body.style.userSelect = prevUserSelect;
        document.body.style.cursor = prevCursor;
      } catch {}
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const allTypes = Object.values(customTypes).flat();
  const allTypesVisible = allTypes.every(type => visibleTypes.has(type));

  const poiLabelToggleBtn = (
    <button
      style={{
        margin: '10px 0 10px 0',
        background: showAllPoiLabels ? '#ffdc5e' : '#232323',
        color: '#232',
        border: 'none',
        borderRadius: 8,
        padding: '7px 16px',
        fontWeight: 700,
        fontSize: 16,
        cursor: 'pointer',
        boxShadow: showAllPoiLabels ? '0 2px 14px #ffd70044' : 'none',
        transition: 'background 0.16s, color 0.16s'
      }}
      onClick={() => setShowAllPoiLabels(v => !v)}
    >
      {showAllPoiLabels ? "Hide all POI labels" : "Show all POI labels"}
    </button>
  );

  const handleMobileTypeClick = (type) => {
    setMobileDrawer(null);
    setFlashType(type);
    setTimeout(() => setFlashType(null), 1500);
  };

  const handleRegionMapClick = (regionMap) => {
    if (!regionMap?.id) return;
    const idStr = String(regionMap.id);
    pushRegionMapRoute:
    {
      const numericTail = idStr.match(/(\d+)$/)?.[1];
      const target = numericTail ? idStr : `${idStr}_${communityMapId || ''}`;
      if (String(mapId) === (numericTail || idStr)) break pushRegionMapRoute;
      navigate(`/map/${target}`);
    }
  };

  const handleAddNewRegionMap = () => {
    if (!communityMapId) return;
    fetch(`${API_BASE_URL}/api/maps/${communityMapId}`)
      .then(res => res.json())
      .then(data => {
        setCommunityMapData(data);
        setExtraRegionMaps(data.extra_region_maps || []);
      })
      .catch(() => {});
  };

  function readDragAsset(dt) {
    try {
      if (!dt) return null;
      if (dt.types?.includes("application/pointatlas-asset")) {
        const raw = dt.getData("application/pointatlas-asset");
        if (raw) return JSON.parse(raw);
      }
      if (dt.types?.includes("application/json+pointatlas-asset")) {
        const raw = dt.getData("application/json+pointatlas-asset");
        if (raw) return JSON.parse(raw);
      }
      if (dt.types?.includes("text/pointatlas-asset")) {
        const raw = dt.getData("text/pointatlas-asset");
        if (raw) return JSON.parse(raw);
      }
      if (dt.types?.includes("text/plain")) {
        const raw = dt.getData("text/plain");
        try { if (raw) return JSON.parse(raw); } catch {}
      }
      return null;
    } catch {
      return null;
    }
  }

  function handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  }

  async function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const payload = readDragAsset(e.dataTransfer);
    if (!payload || !mapData?.id) return;
    try {
      const dz = dropZoneRef.current || mainMapRef.current;
      const rect = dz.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) return;

      const fromMouse = lastMouseCoordRef.current && typeof lastMouseCoordRef.current.x === 'number' && typeof lastMouseCoordRef.current.y === 'number';
      let placementX, placementY;

      if (fromMouse) {
        placementX = Math.round(lastMouseCoordRef.current.x);
        placementY = Math.round(lastMouseCoordRef.current.y);
      } else if (leafletMap && typeof leafletMap.containerPointToLatLng === 'function') {
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const latlng = leafletMap.containerPointToLatLng({ x: px, y: py });
        placementX = Math.round(latlng.lng);
        placementY = Math.round(latlng.lat);
      } else {
        placementX = Math.round(e.clientX - rect.left);
        placementY = Math.round(e.clientY - rect.top);
      }

      let width = 256;
      let height = 256;
      try {
        if (leafletMap && typeof leafletMap.latLngToContainerPoint === 'function') {
          const p0 = leafletMap.latLngToContainerPoint([placementY, placementX]);
          const p1 = leafletMap.latLngToContainerPoint([placementY, placementX + 100]);
          const screenPerImageX = Math.max(1, Math.abs(p1.x - p0.x));
          const imagePerScreen = 1 / screenPerImageX;
          const defaultScreenSize = 200;
          const halfSizeImg = (defaultScreenSize * imagePerScreen) / 2;
          const sw = [placementY - halfSizeImg, placementX - halfSizeImg];
          const ne = [placementY + halfSizeImg, placementX + halfSizeImg];
          width = Math.max(0.00001, ne[1] - sw[1]);
          height = Math.max(0.00001, ne[0] - sw[0]);
        }
      } catch {}

      const title = payload.title || payload.name || "Asset";

      const body = {
        map_id: mapData.id,
        type: payload.asset_type || payload.type || "overlay",
        asset_type: payload.asset_type || payload.type || "overlay",
        title,
        name: title,
        url: payload.url || "",
        visible: 1,
        opacity: payload.opacity ?? 1,
        z_index: payload.z_index ?? 0,
        blocks_vision: payload.blocks_vision ?? 0,
        los_points: payload.los_points ?? null,
        anchor_x: 0.5,
        anchor_y: 0.5,
        placement_x: placementX,
        placement_y: placementY,
        placement_width: width,
        placement_height: height
      };

      const res = await fetch(`${API_BASE_URL}/api/tabletop/assets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to create asset");

      try {
        window.dispatchEvent(new CustomEvent("tabletop-assets-updated", { detail: { mapId: mapData.id } }));
      } catch {}
    } catch {}
  }

  useEffect(() => {
    const onDragOverWindow = (e) => {
      const container = mainMapRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (inside) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      }
    };
    const onDropWindow = (e) => {
      const container = mainMapRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (inside) {
        e.preventDefault();
        e.stopPropagation();
        handleDrop(e);
      }
    };
    window.addEventListener('dragover', onDragOverWindow, true);
    window.addEventListener('drop', onDropWindow, true);
    return () => {
      window.removeEventListener('dragover', onDragOverWindow, true);
      window.removeEventListener('drop', onDropWindow, true);
    };
  }, [mapData?.id, leafletMap]);

  const renderMobileDrawer = () => {
    if (!mobileDrawer) return null;
    return (
      <div className="mobile-drawer-overlay" onClick={() => setMobileDrawer(null)}>
        <div
          className="mobile-drawer"
          onClick={e => e.stopPropagation()}
          style={{ position: "relative" }}
        >
          <button
            className="sidebar-mobile-close"
            onClick={() => setMobileDrawer(null)}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 60,
              right: 17,
              width: 48,
              height: 48,
              border: 'none',
              background: '#212326',
              color: '#fff',
              fontSize: '2em',
              fontWeight: 900,
              zIndex: 4002,
              lineHeight: 1,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s, color 0.13s',
              boxShadow: '0 2px 14px #000b',
            }}
          >×</button>
          {mobileDrawer === "sidebar" && (
            <Sidebar
              key={communityMapData?.id}
              sidebarWidth={320}
              sidebarRef={sidebarRef}
              logo={logo}
              mapDisplayName={communityMapData.name}
              voteStatus={voteStatus}
              handleVote={handleVote}
              user={user}
              isFavourited={isFavourited}
              favLoading={favLoading}
              handleFavourite={handleFavourite}
              customCategories={customCategories}
              customTypes={customTypes}
              typeColours={typeColours}
              visibleTypes={visibleTypes}
              visibleCategories={visibleCategories}
              hoveredType={hoveredType}
              setHoveredType={setHoveredType}
              handleTypeHover={(type) => setHoveredType(type)}
              handleTypeUnhover={() => setHoveredType(null)}
              hoveredPoiId={hoveredPoiId}
              setHoveredPoiId={setHoveredPoiId}
              expandedCategories={expandedCategories}
              setExpandedCategories={setExpandedCategories}
              expandedTypes={expandedTypes}
              setExpandedTypes={setExpandedTypes}
              toggleAllTypes={toggleAllTypes}
              allTypesVisible={allTypesVisible}
              toggleCategoryExpand={toggleCategoryExpand}
              toggleCategory={toggleCategory}
              toggleType={toggleType}
              handleMouseDown={() => { }}
              pois={pois}
              tipUrl={communityMapData.tip_url}
              poiLabelToggleBtn={poiLabelToggleBtn}
              flashType={flashType}
              setFlashType={setFlashType}
              onMobileTypeClick={handleMobileTypeClick}
              isMobile={true}
              onClose={() => setMobileDrawer(null)}
              zones={zones}
              visibleZones={visibleZones}
              setVisibleZones={setVisibleZones}
              toggleZone={toggleZone}
              toggleAllZones={toggleAllZones}
              zonesExpanded={zonesExpanded}
              toggleZonesExpand={toggleZonesExpand}
              hoveredZoneId={hoveredZoneId}
              setHoveredZoneId={setHoveredZoneId}
              hideSidebarHover={hideSidebarHover}
              setHideSidebarHover={setHideSidebarHover}
              extraRegionMaps={extraRegionMaps}
              isMapOwner={isMapOwnerOrFounder}
              onAddNewRegionMap={handleAddNewRegionMap}
              onRegionMapClick={handleRegionMapClick}
              parentMapId={communityMapData.id}
              mapId={mapData.id}
              mapPresenceUsers={mapPresenceUsers}
              onlineMapUserIds={onlineMapUserIds}
              mapPresenceUpdatedAt={mapPresenceUpdatedAt}
              proximityVoiceEnabled={proximityVoiceEnabled}
              proximityVoiceSaving={proximityVoiceSaving}
              proximityVoiceStatus={proximityVoiceStatus}
              onToggleProximityVoice={handleProximityVoiceToggle}
            />
          )}
        </div>
      </div>
    );
  };

  const renderMobileTabBar = () => (
    <div id="mobile-tab-bar" className="mobile-tab-bar">
      <button
        className={`mobile-tab ${mobileDrawer === "sidebar" ? "active" : ""}`}
        onClick={() => setMobileDrawer("sidebar")}
      >
        <span style={{ fontSize: 26, display: "block" }}>☰</span>
      </button>
    </div>
  );

  const mainBgColour = "#000";

  if (!mapData || !imageSize || !communityMapData)
    return (
      <div
        style={{
          color: "#000",
          fontSize: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#000"
        }}
      >
        Map Loading…
      </div>
    );

  const mapDisplayName = mapData.name || '';
  const gameName = gameData?.name || '';
  const pageTitle = gameName && mapDisplayName ? `${gameName} Interactive Map - ${mapDisplayName} | Point Atlas` : 'Point Atlas';
  const pageDescription = gameData?.description
    ? `${gameName} interactive map: ${gameData.description}`
    : `Explore an interactive map for ${gameName || 'your game'} on Point Atlas.`;
  const pageKeywords = `${gameName}, interactive map, ${mapDisplayName}, game maps, point atlas`;

  const sidebarTabOffset = sidebarOpen ? sidebarWidth + 36 : 36;

  const isVideo = !!mapData?.image_url && /\.(mp4|webm|ogg)$/i.test(mapData.image_url);

  return (
    <div className="mapview-mode">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        {!isPublic && <meta name="robots" content="noindex" />}
      </Helmet>

      <div
        ref={mainMapRef}
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          background: mainBgColour,
          overflow: "hidden",
          position: 'relative'
        }}
      >
        {!isMobile && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row', zIndex: 110, position: "relative" }}>
              {sidebarOpen && (
                <div
                  style={{
                    position: "relative",
                    width: `${sidebarWidth}px`,
                    flex: `0 0 ${sidebarWidth}px`,
                    height: "100vh",
                    minWidth: `${SIDEBAR_MIN_WIDTH}px`,
                    maxWidth: `${SIDEBAR_MAX_WIDTH}px`,
                    overflow: "hidden"
                  }}
                >
                  <Sidebar
                    key={communityMapData?.id}
                    sidebarWidth={sidebarWidth}
                    sidebarRef={sidebarRef}
                    logo={logo}
                    mapDisplayName={communityMapData.name}
                    voteStatus={voteStatus}
                    handleVote={handleVote}
                    user={user}
                    isFavourited={isFavourited}
                    favLoading={favLoading}
                    handleFavourite={handleFavourite}
                    customCategories={customCategories}
                    customTypes={customTypes}
                    typeColours={typeColours}
                    visibleTypes={visibleTypes}
                    visibleCategories={visibleCategories}
                    hoveredType={hoveredType}
                    setHoveredType={setHoveredType}
                    handleTypeHover={(type) => setHoveredType(type)}
                    handleTypeUnhover={() => setHoveredType(null)}
                    hoveredPoiId={hoveredPoiId}
                    setHoveredPoiId={setHoveredPoiId}
                    expandedCategories={expandedCategories}
                    setExpandedCategories={setExpandedCategories}
                    expandedTypes={expandedTypes}
                    setExpandedTypes={setExpandedTypes}
                    toggleAllTypes={toggleAllTypes}
                    allTypesVisible={allTypesVisible}
                    toggleCategoryExpand={toggleCategoryExpand}
                    toggleCategory={toggleCategory}
                    toggleType={toggleType}
                    handleMouseDown={handleSidebarMouseDown}
                    pois={pois}
                    tipUrl={communityMapData.tip_url}
                    poiLabelToggleBtn={poiLabelToggleBtn}
                    flashType={flashType}
                    setFlashType={setFlashType}
                    zones={zones}
                    visibleZones={visibleZones}
                    setVisibleZones={setVisibleZones}
                    toggleZone={toggleZone}
                    toggleAllZones={toggleAllZones}
                    zonesExpanded={zonesExpanded}
                    toggleZonesExpand={toggleZonesExpand}
                    hoveredZoneId={hoveredZoneId}
                    setHoveredZoneId={setHoveredZoneId}
                    hideSidebarHover={hideSidebarHover}
                    setHideSidebarHover={setHideSidebarHover}
                    extraRegionMaps={extraRegionMaps}
                    isMapOwner={isMapOwnerOrFounder}
                    onAddNewRegionMap={handleAddNewRegionMap}
                    onRegionMapClick={handleRegionMapClick}
                    parentMapId={communityMapData.id}
                    mapId={mapData.id}
                    mapPresenceUsers={mapPresenceUsers}
                    onlineMapUserIds={onlineMapUserIds}
                    mapPresenceUpdatedAt={mapPresenceUpdatedAt}
                    proximityVoiceEnabled={proximityVoiceEnabled}
                    proximityVoiceSaving={proximityVoiceSaving}
                    proximityVoiceStatus={proximityVoiceStatus}
                    onToggleProximityVoice={handleProximityVoiceToggle}
                  />

                  <div
                    onMouseDown={handleSidebarMouseDown}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 10,
                      height: "100%",
                      cursor: "col-resize",
                      zIndex: 2500,
                      background: "transparent",
                      userSelect: "none",
                      touchAction: "none"
                    }}
                  />
                </div>
              )}
            </div>
          </>
        )}

        <div
          ref={dropZoneRef}
          style={{
            flex: "1 1 0%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <InteractiveMap
            key={mapKey}
            leafletMap={leafletMap}
            setLeafletMap={setLeafletMap}
            mapData={mapData}
            imageSize={imageSize}
            pois={pois}
            foundPOIs={foundPOIs}
            showFound={showFound}
            setShowFound={setShowFound}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            hoveredType={hoveredType}
            hoveredPoiId={hoveredPoiId}
            handleTypeHover={(type) => setHoveredType(type)}
            handleTypeUnhover={() => setHoveredType(null)}
            setHoveredPoiId={setHoveredPoiId}
            pendingPOI={pendingPOI}
            isMapOwnerOrFounder={isMapOwnerOrFounder}
            handleMapCoordClick={handleMapCoordClick}
            handleClick={handleClick}
            mouseCoord={mouseCoord}
            handleMouseCoord={handleMouseCoord}
            shareCoord={shareCoord}
            handleCopyCoord={handleCopyCoord}
            copiedCoord={copiedCoord}
            customCategories={customCategories}
            typeColours={typeColours}
            customTypes={customTypes}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            typeColourInput={typeColourInput}
            setTypeColourInput={setTypeColourInput}
            shape={shape}
            setShape={setShape}
            fill={fill}
            opacity={opacity}
            pattern={pattern}
            description={description}
            setDescription={setDescription}
            savePOI={savePOI}
            showAllPoiLabels={showAllPoiLabels}
            setShowAllPoiLabels={setShowAllPoiLabels}
            onToggleFound={togglePOIFound}
            onCategoryUpdate={handleCategorySaved}
            user={user}
            mapOwnerId={communityMapData.uploader_id}
            flashType={flashType}
            activeTool={activeTool}
            pencilSize={pencilSize}
            pendingLayer={pendingLayer}
            visibleZoneIds={visibleZones}
            onZonesChanged={handleZonesChanged}
            hideSidebarHover={hideSidebarHover}
            setHideSidebarHover={setHideSidebarHover}
            hoveredZoneId={hoveredZoneId}
            sidebarTabOffset={sidebarTabOffset}
            isVideo={isVideo}
            extraRegionMaps={extraRegionMaps}
            parentMapId={communityMapData.id}
            mapPresenceUsers={mapPresenceUsers}
            onlineMapUserIds={onlineMapUserIds}
            mapPresenceUpdatedAt={mapPresenceUpdatedAt}
          />

          {isMapOwnerOrFounder && !isMobile && (
            <div style={{ position: 'absolute', top: 20, right: 40, zIndex: 1000, background: '#222b', padding: '12px 20px', borderRadius: 14, boxShadow: '0 2px 10px #0005', color: '#fff' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  disabled={publishing}
                  onChange={handlePublicToggle}
                  style={{ transform: 'scale(1.3)' }}
                />
                <span style={{ fontWeight: 600, fontSize: '1.06em' }}>{isPublic ? "Map is PUBLIC" : "Map is HIDDEN"}</span>
              </label>
              <div style={{ fontSize: 13, color: pubStatusMsg.includes("now PUBLIC") ? "#9ffb9f" : "#fff", minHeight: 20, marginTop: 4 }}>{pubStatusMsg}</div>
            </div>
          )}

          <div style={{
            position: 'absolute',
            top: 2,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1200,
            background: '#222d',
            color: '#ffe670',
            fontWeight: 700,
            borderRadius: 7,
            padding: '6px 17px 6px 13px',
            fontFamily: 'monospace',
            fontSize: 15,
            pointerEvents: 'none',
            boxShadow: '0 1px 6px #0005',
            letterSpacing: 1,
            display: mouseCoord ? 'block' : 'none'
          }}>
            {mouseCoord &&
              <>X: {Math.round(mouseCoord.x)}<span style={{ margin: '0 8px' }} />Y: {Math.round(mouseCoord.y)}</>
            }
          </div>
        </div>
      </div>

      <SaveLayerModal
        open={showSaveLayerModal}
        onSave={({ name, isPublic }) => {
          setPendingLayer({ name, isPublic });
          setShowSaveLayerModal(false);
        }}
        onCancel={() => setShowSaveLayerModal(false)}
      />

      {isMobile && renderMobileDrawer()}
      {isMobile && renderMobileTabBar()}
    </div>
  );
}

export default MapView;